import cloneDeep from 'lodash.clonedeep';
import { ICustomer } from '../../../data/entities/customer';
import { IFinancial } from '../../../data/entities/customer/financial';
import { SupportedPaymentTypes } from './payment/CustomerPaymentDetails';

const FIELD_TO_TAB: { [key: string]: Array<keyof ICustomer | keyof IFinancial> } = {
	details: [
		'name',
		'email',
		'phone',
		'invoice_email'
	],
	business: [
		'company_name',
		'correspondence_address1',
		'correspondence_country_code',
		'correspondence_city',
		'correspondence_postcode',
		'sort_code',
		'account_holder_name',
		'account_number',
		'swift',
		'iban',
		'paypal_account',
		'payment_method'
	],
};

export enum RegexFields {
	EMAIL,
	SORT_CODE,
	ACCOUNT_NUMBER,
	IBAN,
	SWIFT,
	PHONE
}

export interface IValidationRule {
	level: 'optional' | 'mandatory';
	mandatoryForPaymentType?: SupportedPaymentTypes;
	min?: number;
	max?: number;
	regex?: RegexFields;
	desc?: string;
	quantity?: {
		min?: number;
		max?: number;
	};
}

const REQUIREMENTS = {
	name: {
		level: 'mandatory',
		min: 2,
		desc: 'Your name should be at least 2 characters long',
	},
	email: {
		level: 'mandatory',
		min: 6,
		regex: RegexFields.EMAIL,
		desc: 'Email should be at least 6 characters long, and follow the x@x.x format'
	},
	phone: {
		level: 'mandatory',
		min: 8,
		desc: 'Phone number must be at least 6 digits long'
	},
	company_name: {
		level: 'optional',
		max: 100,
		desc: 'Company name should be 100 characters or fewer'
	},
	correspondence_address1: {
		level: 'mandatory',
		min: 2,
		desc: 'You must specify at least one address'
	},
	payment_method: {
		level: 'mandatory',
		desc: 'You must select a valid payment method',
	},
	correspondence_country_code: {
		level: 'mandatory',
		desc: 'You must specify a country'
	},
	correspondence_city: {
		level: 'mandatory',
		desc: 'You must specify a city'
	},
	correspondence_postcode: {
		level: 'mandatory',
		desc: 'You must specify a postcode'
	},
	account_holder_name: {
		level: 'optional',
		min: 3,
		desc: 'Account holder name should be at least 3 characters long',
		mandatoryForPaymentType: [SupportedPaymentTypes.UK_BANK, SupportedPaymentTypes.INTL_BANK, SupportedPaymentTypes.NON_IBAN]
	},
	sort_code: {
		level: 'optional',
		min: 6,
		max: 6,
		regex: RegexFields.SORT_CODE,
		desc: 'Sort code should be 6 digits (with no spaces or dashes)',
		mandatoryForPaymentType: SupportedPaymentTypes.UK_BANK
	},
	account_number: {
		level: 'optional',
		min: 8,
		max: 8,
		desc: 'Account number should be 8 digits',
		mandatoryForPaymentType: SupportedPaymentTypes.UK_BANK
	},
	intl_account_number: {
		level: 'optional',
		min: 1,
		desc: 'Please enter an account number',
		mandatoryForPaymentType: SupportedPaymentTypes.NON_IBAN
	},
	iban: {
		level: 'optional',
		regex: RegexFields.IBAN,
		desc: 'Please enter a valid IBAN code (with no spaces)',
		mandatoryForPaymentType: SupportedPaymentTypes.INTL_BANK
	},
	swift: {
		level: 'optional',
		regex: RegexFields.SWIFT,
		desc: 'Please enter a valid SWIFT code',
		mandatoryForPaymentType: SupportedPaymentTypes.INTL_BANK
	},
	invoice_email: {
		level: 'optional',
		min: 6,
		regex: RegexFields.EMAIL,
		desc: 'Email should be at least 6 characters long, and follow the x@x.x format'
	}
};

const emailRegexParts = [
	`^[\\w"]+`, // Any of (<word-char-set>, ") at the start 1+ times
	`(?:[\\.\\+\\_\\-]?[\\w"]+)*`, // (Any of .+_- 0-1 times, then any of (<word-char-set>, ") 1+ times) 0+ times
	`@`,
	`\\w+`, // Any <word-char-set> char 1+ times
	`(?:[\\.\\+\\_\\-]?([a-z]{2,}|\\d{3}))*`, // (Any of .+_- 0-1 times, then EITHER a-z 2+ times OR 3 digits) 0+ times
	`\\.(?:([a-z]{2,}|\\d{3}))$`, // A dot then EITHER a-z 2+ times OR 3 digits
];

const regexFieldTests: { [key in RegexFields]: RegExp } = {
	// Email Regex:

	[RegexFields.EMAIL]: new RegExp(emailRegexParts.join('')),
	[RegexFields.SORT_CODE]: new RegExp(/^\d{6}$/),
	[RegexFields.ACCOUNT_NUMBER]: new RegExp(/^\d{8}$/),
	// https://en.wikipedia.org/wiki/International_Bank_Account_Number#Structure
	[RegexFields.IBAN]: new RegExp(/^(?!(RU|BY))[A-Z]{2}\d{2}[\dA-Z]{1,30}$/),
	// https://en.wikipedia.org/wiki/ISO_9362#Structure
	[RegexFields.SWIFT]: new RegExp(/^[A-Z]{4}[A-Z]{2}[A-Z\d]{2}([A-Z0-9]{3})?$/),
	[RegexFields.PHONE]: new RegExp(/^[\+\d\s\(\)-]$/)
};

const validateField = (rule: IValidationRule = {} as any, value: any) => {
	const result: IValidationResult['result'] = {
		valid: true,
		validated: true,
		present: true,
		min: true,
		max: true,
		regex: true,
		desc: 'Unknown Error'
	};

	const present = !!value || (value !== null && typeof value === 'number');
	result.present = present;

	const length = present ? typeof value === 'number' ? value : value.length : 0;
	const min = rule.min ? length >= rule.min : true;
	const max = rule.max ? length <= rule.max : true;

	const regex = rule.regex !== undefined ? regexFieldTests[rule.regex].test(value) : true;

	result.min = min;
	result.max = max;
	result.regex = regex;
	result.desc = rule.desc || '';

	if (present) {

		if (rule.regex !== undefined && !regex) {
			result.valid = false;
		}

		if (rule.min && !min) {
			result.valid = false;
		}

		if (rule.max && !max) {
			result.valid = false;
		}
	}

	if (rule.level && rule.level === 'mandatory' && !present) {
		result.valid = false;
	}

	return result;
};

export interface IValidationResult {
	key: string;
	tab?: string;
	result: {
		valid: boolean;
		validated: boolean;
		present?: boolean;
		min?: boolean;
		max?: boolean;
		regex?: boolean;
		desc?: string;
	};
}

export const validateCustomer = (customer: ICustomer): IValidationResult[] => {
	return Object.keys(customer).map((key) => {
		const rule = REQUIREMENTS[key];

		const tab = Object.keys(FIELD_TO_TAB).find((t) => FIELD_TO_TAB[t].includes(key as keyof ICustomer));
		const result = {
			key,
			tab,
			result: rule ? validateField(rule, customer[key]) :
				{ valid: true, validated: false }
		};

		return result;
	});
};

// TODO don't validate fields that aren't shown (in the selected payment type)
export const validateFinancial = (financial: IFinancial): IValidationResult[] => {
	return Object.keys(financial || {}).map((key) => {
		const rule = cloneDeep(REQUIREMENTS[key]);

		// Dynamically mark this field mandatory if the payment type requires it
		if (rule && rule.mandatoryForPaymentType &&
			financial.payment_method === rule.mandatoryForPaymentType) {
			rule.level = 'mandatory';
		}

		const tab = Object.keys(FIELD_TO_TAB).find((t) => FIELD_TO_TAB[t].includes(key as keyof IFinancial));
		const result = {
			key,
			tab,
			result: rule ? validateField(rule, financial[key]) :
				{ valid: true, validated: false }
		};

		return result;
	});
};
